import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrencyDetection, formatPrice } from '../utils/currency';

const SubscriptionPage = () => {
  const [plans, setPlans] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userCurrency = useCurrencyDetection();

  // Get URL parameters
  const selectedPlanFromUrl = searchParams.get('plan');
  const signupComplete = searchParams.get('signup');

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  // Handle signup completion and auto-select plan
  useEffect(() => {
    if (signupComplete === 'complete' && selectedPlanFromUrl) {
      // Auto-trigger subscription for the selected plan
      setTimeout(() => {
        handleSubscribe(selectedPlanFromUrl);
      }, 1000); // Small delay to ensure page is loaded
    }
  }, [signupComplete, selectedPlanFromUrl]);

  const loadSubscriptionData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token'); // Fixed: Use correct token key
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add auth header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Load subscription plans (available to everyone)
      const plansResponse = await fetch(`${backendUrl}/api/subscription/plans`, { headers });
      if (plansResponse.ok) {
        const plansData = await plansResponse.json();
        setPlans(plansData);
      }

      // Load subscription status only if authenticated
      if (token) {
        const statusResponse = await fetch(`${backendUrl}/api/subscription/status`, { headers });
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setSubscriptionStatus(statusData);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      setError('Failed to load subscription information');
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    const token = localStorage.getItem('dcTech_token'); // Fixed: Use correct token key
    
    console.log('🔍 Subscription clicked:', planId);
    console.log('🔍 Token exists:', !!token);
    
    // If not authenticated, redirect to sign up as provider
    if (!token) {
      console.log('❌ No token, redirecting to auth');
      navigate('/auth?redirect=subscription&plan=' + planId);
      return;
    }

    setPaymentLoading(true);
    setError(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const hostUrl = window.location.origin;

      console.log('🔍 Making API call to:', `${backendUrl}/api/subscription/create`);
      console.log('🔍 Request data:', { planId, hostUrl });

      const response = await fetch(`${backendUrl}/api/subscription/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planId: planId,
          hostUrl: hostUrl
        })
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Subscription response data:', data);
        console.log('🔍 Redirecting to:', data.url);
        
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        console.error('❌ Subscription error:', errorData);
        setError(errorData.detail || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('❌ Exception during subscription:', error);
      setError('Failed to create subscription: ' + error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-700 hover:text-gray-900 mb-6 transition-colors"
        >
          <svg 
            className="w-5 h-5 mr-2" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 19l-7-7 7-7" 
            />
          </svg>
          <span className="font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Choose Your Professional Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join our professional marketplace and grow your business across multiple trades
          </p>
        </div>

        {/* Current Subscription Status */}
        {subscriptionStatus && subscriptionStatus.status === 'active' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-green-800">Subscription Active</h3>
                <p className="text-green-600">
                  Your {subscriptionStatus.plan} plan is active until {new Date(subscriptionStatus.expiresAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden transform   ${
                plan.id === 'premium_monthly' ? 'ring-2 ring-purple-500' : ''
              } ${plan.id === 'enterprise_monthly' ? 'ring-2 ring-gold-500' : ''}`}
            >
              {plan.id === 'premium_monthly' && (
                <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white text-center py-2 text-sm font-semibold">
                  MOST POPULAR
                </div>
              )}
              {plan.id === 'enterprise_monthly' && (
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-center py-2 text-sm font-semibold">
                  ENTERPRISE
                </div>
              )}
              {plan.id === 'starter_monthly' && (
                <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white text-center py-2 text-sm font-semibold">
                  BEST FOR STARTERS
                </div>
              )}
              
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="text-4xl font-bold text-gray-800">
                    {plan.price_info ? (
                      <>
                        {plan.price_info.local_formatted}
                        {plan.price_info.local_currency !== 'USD' && (
                          <div className="text-sm text-gray-500 mt-1">
                            (${plan.price_info.original_usd} USD)
                          </div>
                        )}
                      </>
                    ) : (
                      `$${plan.price}`
                    )}
                    <span className="text-lg font-normal text-gray-500">/month</span>
                  </div>
                  {!userCurrency.loading && userCurrency.currency !== 'USD' && (
                    <p className="text-sm text-blue-600 mt-2">
                      💱 Converted to {userCurrency.currency} based on your location ({userCurrency.country})
                    </p>
                  )}
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={paymentLoading || (subscriptionStatus && subscriptionStatus.status === 'active')}
                  className={`w-full py-3 px-6 rounded-lg font-semibold text-white  ${
                    subscriptionStatus && subscriptionStatus.status === 'active'
                      ? 'bg-gray-400 cursor-not-allowed'
                      : plan.id === 'premium_monthly'
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                      : plan.id === 'enterprise_monthly'
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                      : plan.id === 'starter_monthly'
                      ? 'bg-gradient-to-r from-green-500 to-teal-500'
                      : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                  }`}
                >
                  {paymentLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : subscriptionStatus && subscriptionStatus.status === 'active' ? (
                    'Current Plan'
                  ) : (
                    plan.price_info ? 
                      `Subscribe for ${plan.price_info.local_formatted}/month` :
                      `Subscribe for $${plan.price}/month`
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Why Join Our Professional Marketplace?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Multi-Trade Support</h3>
              <p className="text-gray-600">Offer services across electrical, plumbing, HVAC, roofing, and more</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Secure Payments</h3>
              <p className="text-gray-600">Get paid securely through our integrated payment system</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Build Your Reputation</h3>
              <p className="text-gray-600">Collect reviews and build your professional reputation</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;